/**
 * a11y-toolbar.js — floating accessibility button + panel.
 *
 * Click the button to open a panel with two toggles:
 *   · Hoher Kontrast (high-contrast mode)
 *   · Größerer Text  (115% root font scaling)
 *
 * Settings persist in localStorage. Classes applied to <html>
 * (not body) so rem-based sizes and scheme-scoped CSS variables
 * are overridden correctly.
 *
 * Keyboard: Tab to button, Enter/Space opens, Tab cycles toggles,
 * Esc closes, click-outside closes.
 */

(function () {
  'use strict';

  var CONFIG = (window.OBP_A11Y_CONFIG || {});
  if (CONFIG.enabled === false) return;

  var POSITION = CONFIG.position || 'bottom-right';
  var STORAGE_KEY = 'obp_a11y_settings';

  function loadSettings() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { highContrast: false, largeText: false };
      return Object.assign({ highContrast: false, largeText: false }, JSON.parse(raw));
    } catch (e) { return { highContrast: false, largeText: false }; }
  }
  function saveSettings(s) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (e) {}
  }

  // Apply to <html> so html-scoped CSS (rem font-size, root variables)
  // propagates everywhere.
  function applySettings(s) {
    document.documentElement.classList.toggle('a11y-high-contrast', !!s.highContrast);
    document.documentElement.classList.toggle('a11y-large-text',    !!s.largeText);

    // Nuclear button override — Dawn's button styling can be deeply
    // scoped via color-scheme variables and pseudo-element box-shadows
    // which compete with our CSS. Inline styles win over any rule.
    // Excludes the a11y toolbar's own buttons.
    var btnSelector = [
      '.button:not(.a11y-toolbar):not(.a11y-toolbar__reset)',
      'button:not(.a11y-toolbar):not(.a11y-toolbar__reset)',
      'a.button:not(.a11y-toolbar):not(.a11y-toolbar__reset)',
      '.obp-hero__btn',              // custom OBP hero "Zum Shop"
      '.obp-hero__btn--primary',
      '.obp-hero__btn--secondary',
      'a.obp-hero__btn'
    ].join(', ');

    if (s.highContrast) {
      document.querySelectorAll(btnSelector).forEach(function (btn) {
        btn.style.setProperty('background', '#fff',         'important');
        btn.style.setProperty('background-color', '#fff',   'important');
        btn.style.setProperty('background-image', 'none',   'important');
        btn.style.setProperty('color', '#000',              'important');
        btn.style.setProperty('border', '2px solid #000',   'important');
        btn.style.setProperty('box-shadow', 'none',         'important');
      });
    } else {
      document.querySelectorAll(btnSelector).forEach(function (btn) {
        btn.style.removeProperty('background');
        btn.style.removeProperty('background-color');
        btn.style.removeProperty('background-image');
        btn.style.removeProperty('color');
        btn.style.removeProperty('border');
        btn.style.removeProperty('box-shadow');
      });
    }
  }

  function build() {
    var wrap = document.createElement('div');
    wrap.className = 'a11y-toolbar-wrap a11y-toolbar-wrap--' + POSITION;
    wrap.innerHTML =
      '<button type="button" class="a11y-toolbar" aria-label="Bedienungshilfen"' +
        ' aria-expanded="false" aria-controls="a11y-panel">' +
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">' +
          '<path d="M12 2a2 2 0 110 4 2 2 0 010-4zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z"/>' +
        '</svg>' +
      '</button>' +
      '<div class="a11y-toolbar__panel" id="a11y-panel" role="region" aria-label="Anzeige-Einstellungen">' +
        '<h2 class="a11y-toolbar__title">Anzeige</h2>' +
        '<label class="a11y-toolbar__row">' +
          '<input type="checkbox" data-a11y-toggle="highContrast">' +
          '<span>Hoher Kontrast</span>' +
        '</label>' +
        '<label class="a11y-toolbar__row">' +
          '<input type="checkbox" data-a11y-toggle="largeText">' +
          '<span>Größerer Text</span>' +
        '</label>' +
        '<button type="button" class="a11y-toolbar__reset" data-a11y-reset>Zurücksetzen</button>' +
      '</div>';
    document.body.appendChild(wrap);
    return wrap;
  }

  function init() {
    var settings = loadSettings();
    applySettings(settings); // apply BEFORE building UI so no flash

    var wrap    = build();
    var btn     = wrap.querySelector('.a11y-toolbar');
    var panel   = wrap.querySelector('.a11y-toolbar__panel');
    var toggles = wrap.querySelectorAll('[data-a11y-toggle]');
    var reset   = wrap.querySelector('[data-a11y-reset]');

    function refreshActiveStyle() {
      var anyOn = settings.highContrast || settings.largeText;
      btn.classList.toggle('a11y-toolbar--active', anyOn);
    }

    toggles.forEach(function (cb) {
      cb.checked = !!settings[cb.dataset.a11yToggle];
    });
    refreshActiveStyle();

    function openPanel() {
      panel.classList.add('a11y-toolbar__panel--open');
      btn.setAttribute('aria-expanded', 'true');
      var first = panel.querySelector('input,button');
      if (first) first.focus();
    }
    function closePanel() {
      panel.classList.remove('a11y-toolbar__panel--open');
      btn.setAttribute('aria-expanded', 'false');
    }
    function isOpen() { return panel.classList.contains('a11y-toolbar__panel--open'); }
    function togglePanel() { isOpen() ? closePanel() : openPanel(); }

    btn.addEventListener('click', togglePanel);

    toggles.forEach(function (cb) {
      cb.addEventListener('change', function () {
        settings[cb.dataset.a11yToggle] = cb.checked;
        saveSettings(settings);
        applySettings(settings);
        refreshActiveStyle();
      });
    });

    reset.addEventListener('click', function () {
      settings = { highContrast: false, largeText: false };
      saveSettings(settings);
      applySettings(settings);
      toggles.forEach(function (cb) { cb.checked = false; });
      refreshActiveStyle();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) {
        closePanel();
        btn.focus();
      }
    });
    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target) && isOpen()) closePanel();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
